import type { AutoClaimedTaskDto, DailyTaskDto, DailyTaskRewardDto, DailyTasksPayloadDto } from "../api/dto/daily_tasks.dto.js";
import { featureDateTime, logRewardCoins, rewardsLabel, trackFeatureEvent } from "../analytics/feature_events.js";

function eventId( payload: DailyTasksPayloadDto ): string
{
  return `daily_tasks@${payload.stars?.week_start ?? payload.period_start ?? "unknown"}`;
}

function common( payload: DailyTasksPayloadDto ): Record<string, unknown>
{
  return {
    event_id: eventId( payload ),
    config_version: payload.version ?? null,
    day_index: payload.day_index ?? null,
    date_start: featureDateTime( payload.stars?.week_start ?? payload.period_start ),
    date_end: featureDateTime( payload.stars?.week_end ?? payload.period_end ),
    stars_current: payload.stars?.current ?? 0,
  };
}

function taskParams( task: DailyTaskDto ): Record<string, unknown>
{
  return {
    task_id: task.id,
    task_day: task.day,
    task_metric: task.metric,
    task_param: task.param ?? "",
    task_target: task.target,
    task_progress: task.progress,
    task_stars: task.stars,
    rewards: rewardsLabel( task.rewards ),
  };
}

/**
 * Daily tasks ("7 days") analytics. Everything is derived from the server
 * payload, so a state seen twice (reload, other device, offline replay) never
 * repeats an event: assigned / completed once per task and week, claimed once
 * per task, a chest once per week and index.
 */
export const DailyTasksAnalytics = {
  observe( payload: DailyTasksPayloadDto | null | undefined ): void
  {
    if ( !payload?.enabled ) return;
    const id = eventId( payload );
    trackFeatureEvent( "daily_tasks_started", { ...common( payload ), tasks_total: payload.tasks.length, chests_total: payload.stars?.chests.length ?? 0 }, id );
    for ( const task of payload.tasks )
    {
      trackFeatureEvent( "daily_task_assigned", { ...common( payload ), ...taskParams( task ) }, `${id}|${task.id}` );
      if ( task.completed ) trackFeatureEvent( "daily_task_completed", { ...common( payload ), ...taskParams( task ) }, `${id}|${task.id}` );
      if ( task.claimed ) trackFeatureEvent( "daily_task_claimed", { ...common( payload ), ...taskParams( task ), auto: false }, `${id}|${task.id}` );
    }
  },

  claimed( payload: DailyTasksPayloadDto | null | undefined, task_id: string, rewards: DailyTaskRewardDto[], day?: number ): void
  {
    if ( !payload ) return;
    const task = payload.tasks.find( entry => entry.id === task_id && ( day === undefined || entry.day === day ) );
    if ( !task ) return;
    const sent = trackFeatureEvent( "daily_task_claimed", { ...common( payload ), ...taskParams( task ), rewards: rewardsLabel( rewards.length ? rewards : task.rewards ), auto: false }, `${eventId( payload )}|${task.id}` );
    if ( sent ) logRewardCoins( rewards.length ? rewards : task.rewards, "daily_task" );
  },

  autoClaimed( payload: DailyTasksPayloadDto | null | undefined, claimed: AutoClaimedTaskDto[] ): void
  {
    for ( const item of claimed )
    {
      const sent = trackFeatureEvent( "daily_task_claimed", {
        ...( payload ? common( payload ) : {} ),
        task_id: item.id,
        rewards: rewardsLabel( item.rewards ),
        auto: true,
      }, `auto|${item.id}` );
      if ( sent ) logRewardCoins( item.rewards, "daily_task_auto" );
    }
  },

  chestClaimed( payload: DailyTasksPayloadDto | null | undefined, index: number, rewards: DailyTaskRewardDto[] ): void
  {
    if ( !payload ) return;
    const chest = payload.stars?.chests[index];
    const sent = trackFeatureEvent( "daily_tasks_chest_claimed", {
      ...common( payload ),
      chest_index: index + 1,
      chests_total: payload.stars?.chests.length ?? 0,
      stars_required: chest?.stars ?? null,
      rewards: rewardsLabel( rewards ),
    }, `${eventId( payload )}|chest|${index}` );
    if ( sent ) logRewardCoins( rewards, "daily_tasks_chest" );
  },

  opened( payload: DailyTasksPayloadDto | null | undefined ): void
  {
    if ( !payload?.enabled ) return;
    const claimable = payload.tasks.filter( task => task.completed && !task.claimed ).length
      + ( payload.stars?.chests.filter( chest => chest.claimable && !chest.claimed ).length ?? 0 );
    trackFeatureEvent( "daily_tasks_opened", {
      ...common( payload ),
      tasks_completed: payload.tasks.filter( task => task.completed ).length,
      tasks_total: payload.tasks.length,
      claimable_count: claimable,
    } );
  },
};
